/**
 * @flow
 */

import React, { useCallback } from 'react';
import gql from 'graphql-tag';
import styled from 'styled-components';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { isEmpty, get } from 'lodash';
import { FormattedMessage } from 'react-intl';
import { List, Loader } from 'semantic-ui-react';
import InfiniteScroll from 'react-infinite-scroller';
import { Table, Avatar, EllipsisLoader } from '../../../components';

import ClientDetailsButton from './ClientDetailsButton';
import EditClientButton from './EditClientButton';
import DeleteClientButton from './DeleteClientButton';

import type { Client, ClientEdge, PageInfo } from '../../../type';

export const GET_CLIENTS_QUERY = gql`
    query getClients(
        $salon: ID!
        $search: String
        $first: Int
        $after: String
    ) {
        viewer {
            id
            ... on Administrator {
                salon(id: $salon) {
                    id
                    clients(search: $search, first: $first, after: $after) {
                        pageInfo {
                            endCursor
                            hasNextPage
                        }
                        edges {
                            cursor
                            node {
                                id
                                ...client
                            }
                        }
                    }
                }
            }
        }
    }

    fragment client on Client {
        email
        firstName
        lastName
        phone
        notes
        birthday
        blockedAt
        blockedBy {
            id
            firstName
            lastName
        }
        gender
        type
    }
`;

type ClientsListProps = {
    clients: Array<ClientEdge>,
    loadMore: Function,
    pageInfo: PageInfo,
};

type ClientRowProps = {
    client: Client,
};

const NameCell = styled(Table.Cell)`
    padding: 0px !important;
`;

/**
 * Show this message if clients list is empty
 */
const EmptyMessage = () => (
    <Table.Row className="transparent">
        <Table.Cell colSpan={3} textAlign="center">
            <FormattedMessage
                id="pages.clients.ClientList.noClients"
                defaultMessage="You didn't added any clients yet."
            />
        </Table.Cell>
    </Table.Row>
);

const LoadMoreSpinner = () => (
    <Table.Row className="transparent">
        <Table.Cell colSpan={3} textAlign="center">
            <EllipsisLoader size="1em" />
        </Table.Cell>
    </Table.Row>
);

/**
 * Represents single client row
 */
const ClientRow = ({ client }: ClientRowProps) => {
    if (client.firstName) {
        return (
            <Table.Row>
                <NameCell>
                    <ClientDetailsButton client={client}>
                        <Avatar
                            name={`${client.firstName} ${client.lastName ||
                                ''}`}
                            size={24}
                            spaced="right"
                            round
                        />
                        <span>
                            {client.firstName} {client.lastName}
                        </span>
                    </ClientDetailsButton>
                </NameCell>
                <Table.Cell>{client.phone}</Table.Cell>
                <Table.Cell>
                    <List horizontal divided>
                        <List.Item>
                            <EditClientButton client={client} />
                        </List.Item>
                        <List.Item>
                            <DeleteClientButton client={client} />
                        </List.Item>
                    </List>
                </Table.Cell>
            </Table.Row>
        );
    } else {
        return null;
    }
};

/**
 * Represents list of all salon clients (pure presentation layer)
 */
const ClientsListView = ({ clients, pageInfo, loadMore }: ClientsListProps) => (
    <Table celled selectable striped fixed>
        <Table.Header>
            <Table.Row>
                <Table.HeaderCell>
                    <FormattedMessage
                        id="pages.clients.ClientList.name"
                        defaultMessage="Name"
                    />
                </Table.HeaderCell>
                <Table.HeaderCell>
                    <FormattedMessage
                        id="pages.clients.ClientList.phone"
                        defaultMessage="Phone"
                    />
                </Table.HeaderCell>
                <Table.HeaderCell>
                    <FormattedMessage
                        id="pages.clients.ClientList.actions"
                        defaultMessage="Actions"
                    />
                </Table.HeaderCell>
            </Table.Row>
        </Table.Header>
        <InfiniteScroll
            element="tbody"
            pageStart={0}
            loadMore={loadMore}
            initialLoad={false}
            hasMore={pageInfo.hasNextPage}
            loader={<LoadMoreSpinner key={0} />}
            threshold={1}
            useWindow
        >
            {isEmpty(clients) ? (
                <EmptyMessage />
            ) : (
                clients.map(client => (
                    <ClientRow
                        key={client.cursor}
                        client={client.node}
                        pageInfo={pageInfo}
                    />
                ))
            )}
        </InfiniteScroll>
    </Table>
);

/**
 * Self-contained ClientsList: fetches its own data via useQuery
 */
const ClientsList = () => {
    const salon = useSelector(({ user }) => user.get('salon'));
    const searchQuery = useSelector(({ clients }) =>
        clients.get('searchQuery'),
    );

    const { data, loading, fetchMore } = useQuery(GET_CLIENTS_QUERY, {
        variables: {
            salon,
            search: searchQuery || undefined,
            first: 20,
        },
        fetchPolicy: 'cache-and-network',
    });

    const clients = get(data, 'viewer.salon.clients.edges', []);
    const pageInfo = get(data, 'viewer.salon.clients.pageInfo', {});

    const loadMore = useCallback(() => {
        if (loading || !pageInfo.hasNextPage) return false;
        fetchMore({
            query: GET_CLIENTS_QUERY,
            variables: {
                salon,
                first: 20,
                after: pageInfo.endCursor,
                search: searchQuery || undefined,
            },
            updateQuery: (previousResult, { fetchMoreResult }) => {
                const prevEdges = previousResult.viewer.salon.clients.edges;
                const newEdges = fetchMoreResult.viewer.salon.clients.edges;
                const newResults = Object.assign({}, fetchMoreResult);
                newResults.viewer.salon.clients.edges = [
                    ...prevEdges,
                    ...newEdges,
                ];
                return newResults;
            },
        });
    }, [loading, pageInfo, salon, searchQuery, fetchMore]);

    // Show spinner while the clients list is loading for the first time
    if (loading && isEmpty(clients)) {
        return <Loader size="big" inline="centered" active />;
    }

    return (
        <ClientsListView
            clients={clients}
            pageInfo={pageInfo}
            loadMore={loadMore}
        />
    );
};

export default ClientsList;
